import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    createTheme,
    ThemeProvider as BaseThemeProvider
} from '@mui/material';
import { grey } from "@mui/material/colors";
import { kebabCase, uniqBy } from "lodash";
import axios from "axios";
import memoizee from "memoizee";
import { useDatePickerLocales } from "../../../Common/hooks/datePickerLocales";
import CheckBeforeRequest from "../../../Common/CheckBeforeRequest";
import { setTheme } from "../redux/themeSlice";
import { ThemeObject } from "../../MaterialTripList/objects/themeObject";
import { AppState } from "../../../../Reducers/Reducers";
import GothamThin from "../../../../Fonts/Gotham/Gotham-Thin.otf";
import GothamThinItalic from "../../../../Fonts/Gotham/Gotham-ThinItalic.otf";
import GothamExtraLight from "../../../../Fonts/Gotham/Gotham-ExtraLight.otf";
import GothamExtraLightItalic from "../../../../Fonts/Gotham/Gotham-XLightItalic.otf";
import GothamLight from "../../../../Fonts/Gotham/Gotham-Light.otf";
import GothamLightItalic from "../../../../Fonts/Gotham/Gotham-LightItalic.otf";
import GothamBook from "../../../../Fonts/Gotham/Gotham-Book.otf";
import GothamBookItalic from "../../../../Fonts/Gotham/Gotham-BookItalic.otf";
import GothamMedium from "../../../../Fonts/Gotham/Gotham-Medium.otf";
import GothamMediumItalic from "../../../../Fonts/Gotham/Gotham-MediumItalic.otf";
import GothamBold from "../../../../Fonts/Gotham/Gotham-Bold.otf";
import GothamBoldItalic from "../../../../Fonts/Gotham/Gotham-BoldItalic.otf";
import GothamBlack from "../../../../Fonts/Gotham/Gotham-Black.otf";
import GothamBlackItalic from "../../../../Fonts/Gotham/Gotham-BlackItalic.otf";
import GothamUltra from "../../../../Fonts/Gotham/Gotham-Ultra.otf";
import GothamUltraItalic from "../../../../Fonts/Gotham/Gotham-UltraItalic.otf";
import CircularStdMedium from "../../../../Fonts/circular-std/CircularStd-Medium.woff";
import CircularStdMediumItalic from "../../../../Fonts/circular-std/CircularStd-MediumItalic.woff";
import CircularStdMediumBold from "../../../../Fonts/circular-std/CircularStd-Bold.woff";
import CircularStdMediumBoldItalic from "../../../../Fonts/circular-std/CircularStd-BoldItalic.woff";
import MarkMyWords from "../../../../Fonts/mark_my_words_clean.otf";
import MontSerrat from "../../../../Fonts/MontSerrat/Montserrat-VariableFont_wght.ttf";
import MontSerratItalic from "../../../../Fonts/MontSerrat/Montserrat-Italic-VariableFont_wght.ttf";
import SimpleNails from "../../../../Fonts/Simplesnails ver 4.0.ttf";
import IconVerdie from "../../../../Fonts/IconVerdie.ttf";
import AppleGaramond from "../../../../Fonts/AppleGaramond.ttf";
import MazdaType from "../../../../Fonts/Mazda-Type-Regular.ttf";
import GilmerBold from "../../../../Fonts/Gilmer_Font/Gilmer Bold.otf";
import GilmerHeavy from "../../../../Fonts/Gilmer_Font/Gilmer Heavy.otf";
import GilmerLight from "../../../../Fonts/Gilmer_Font/Gilmer Light.otf";
import GilmerMedium from "../../../../Fonts/Gilmer_Font/Gilmer Medium.otf";
import GilmerOutline from "../../../../Fonts/Gilmer_Font/Gilmer Outline.otf";
import GilmerRegular from "../../../../Fonts/Gilmer_Font/Gilmer Regular.otf";
import LucidaGrandeRegular from "../../../../Fonts/LucidaGrande - Lucida Grande - Regular.ttf";
import MyriadProLight from "../../../../Fonts/MyriadPro/MyriadPro-Light.otf";
import MyriadProRegular from "../../../../Fonts/MyriadPro/MYRIADPRO-REGULAR.otf";
import MyriadProSemiBold from "../../../../Fonts/MyriadPro/MYRIADPRO-SEMIBOLD.otf";
import MyriadProSemiBoldItalic from "../../../../Fonts/MyriadPro/MYRIADPRO-SEMIBOLDIT.otf";
import MyriadProBold from "../../../../Fonts/MyriadPro/MYRIADPRO-BOLD.otf";
import MyriadProBoldItalic from "../../../../Fonts/MyriadPro/MYRIADPRO-BOLDIT.otf";
import SweetSansPro from "../../../../Fonts/SweetSansPro/fonnts.com-SweetSansProRegular.otf";

type Props = {
    children: React.ReactNode
}

export const BREAKPOINTS = {
    xs: 0,
    sm: 345,
    md: 767,
    lg: 1024,
    xl: 1536
} as const;

export function ThemeProvider(props: Props): JSX.Element {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const themeObject = useSelector((state: AppState) => state.theme.theme);
    const pickerText = useDatePickerLocales(
        (() => {
            if (i18n.language.includes('fr')) {
                return 'frFR';
            } else if (i18n.language.includes('en')) {
                return 'enUS';
            } else if (i18n.language.includes('es')) {
                return 'esES';
            }
            return 'frFR';
        })()
    );
    const theme = useMemo(() => {
        const themeOptions = themeObject?.data;

        function typographyToCss(selector: string, styles: object) {
            const content = Object.keys(styles).map((key) => {
                return `${kebabCase(key)}: ${(styles as any)[key]}`
            }).join(';');
            return `
                ${selector} {
                    ${content}
                }
            `;
        }

        return createTheme(
            {
                breakpoints: {
                    values: BREAKPOINTS
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: `
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 100;
                        src: url('${GothamThin}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 100;
                        src: url('${GothamThinItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 200;
                        src: url('${GothamExtraLight}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 200;
                        src: url('${GothamExtraLightItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 300;
                        src: url('${GothamLight}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 300;
                        src: url('${GothamLightItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 400;
                        src: url('${GothamBook}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 400;
                        src: url('${GothamBookItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 500;
                        src: url('${GothamMedium}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 500;
                        src: url('${GothamMediumItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 700;
                        src: url('${GothamBold}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 700;
                        src: url('${GothamBoldItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 800;
                        src: url('${GothamBlack}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 800;
                        src: url('${GothamBlackItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: normal;
                        font-weight: 900;
                        src: url('${GothamUltra}') format('opentype');
                    }
                    @font-face {
                        font-family: Gotham;
                        font-style: italic;
                        font-weight: 900;
                        src: url('${GothamUltraItalic}') format('opentype');
                    }
                    @font-face {
                        font-family: SimpleNails;
                        src: url('${SimpleNails}')  format('truetype');
                    }

                    @font-face {
                        font-family: CircularStd;
                        src: url('${CircularStdMedium}') format('woff');
                    }
                    @font-face {
                        font-family: CircularStd;
                        font-weight: bold;
                        src: url('${CircularStdMediumBold}') format('woff');
                    }
                    @font-face {
                        font-family: CircularStd;
                        font-style: italic;
                        src: url('${CircularStdMediumItalic}') format('woff');
                    }
                    @font-face {
                        font-family: CircularStd;
                        font-weight: bold;
                        font-style: italic;
                        src: url('${CircularStdMediumBoldItalic}') format('woff');
                    }

                    @font-face {
                        font-family: MarkMyWords;
                        font-weight: 400;
                        src: url('${MarkMyWords}') format('opentype');
                    }

                    @font-face {
                        font-family: IconVerdie;
                        src: url('${IconVerdie}')  format('truetype');
                    }

                    @font-face {
                        font-family: MontSerrat;
                        font-weight: 400;
                        src: url('${MontSerrat}')  format('truetype');
                    }

                    @font-face {
                        font-family: MontSerrat;
                        font-weight: 400;
                        src: url('${MontSerratItalic}')  format('truetype');
                        font-style: italic;
                    }

                    @font-face {
                        font-family: AppleGaramond;
                        font-weight: 400;
                        src: url('${AppleGaramond}')  format('truetype');
                    }

                    @font-face {
                        font-family: MazdaType;
                        font-weight: 400;
                        src: url('${MazdaType}')  format('truetype');
                    }

                    @font-face {
                        font-family: Gilmer;
                        src: url('${GilmerLight}')  format('opentype');
                        font-weight: 300;
                    }

                    @font-face {
                        font-family: Gilmer;
                        src: url('${GilmerRegular}')  format('opentype'),
                             url('${GilmerOutline}')  format('opentype');
                        font-weight: 400;
                    }

                    @font-face {
                        font-family: Gilmer;
                        src: url('${GilmerMedium}')  format('opentype');
                        font-weight: 500;
                    }

                    @font-face {
                        font-family: Gilmer;
                        font-weight: 700;
                        src: url('${GilmerBold}')  format('opentype');
                    }

                    @font-face {
                        font-family: Gilmer;
                        src: url('${GilmerHeavy}')  format('opentype');
                        font-weight: 900;
                    }

                    @font-face {
                        font-family: LucidaGrande;
                        font-weight: 400;
                        src: url('${LucidaGrandeRegular}')  format('truetype');
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProLight}')  format('opentype');
                        font-weight: 300;
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProRegular}')  format('opentype');
                        font-weight: 400;
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProSemiBoldItalic}')  format('opentype');
                        font-weight: 600;
                        font-style: italic;
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProSemiBold}')  format('opentype');
                        font-weight: 600;
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProBoldItalic}')  format('opentype');
                        font-weight: 700;
                        font-style: italic;
                    }

                    @font-face {
                        font-family: MyriadPro;
                        src: url('${MyriadProBold}')  format('opentype');
                        font-weight: 700;
                    }

                    @font-face {
                        font-family: SweetSansPro;
                        font-weight: 400;
                        src: url('${SweetSansPro}')  format('opentype');
                    }

                    a {
                        color: ${themeOptions?.colors?.secondary ?? 'initial'};
                        text-decoration: none;
                    }

                    ${typographyToCss('h1', themeOptions?.typography?.h1 ?? {})}
                    ${typographyToCss('h2', themeOptions?.typography?.h2 ?? {})}
                    ${typographyToCss('h3', themeOptions?.typography?.h3 ?? {})}
                    ${typographyToCss('h4', themeOptions?.typography?.h4 ?? {})}
                    ${typographyToCss('h5', themeOptions?.typography?.h5 ?? {})}
                    ${typographyToCss('h6', themeOptions?.typography?.h6 ?? {})}
                `
                    },
                    MuiButtonBase: {
                        styleOverrides: {
                            root: {
                                fontFamily: themeOptions?.typography?.body1.fontFamily ?? 'Gotham'
                            }
                        }
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                fontFamily: themeOptions?.typography?.body1.fontFamily ?? 'Gotham'
                            }
                        }
                    },
                    MuiTab: {
                        styleOverrides: {
                            root: {
                                fontFamily: themeOptions?.typography?.body1.fontFamily ?? 'Gotham'
                            }
                        }
                    },
                },
                palette: {
                    primary: {
                        main: themeOptions?.colors?.primary ?? '#B7985A',
                        contrastText: '#fff'
                    },
                    secondary: {
                        main: themeOptions?.colors?.secondary ?? '#003B6F',
                        contrastText: '#fff'
                    },
                    default: {
                        main: grey[300],
                        dark: grey[400]
                    },
                    pageBuilder: {
                        main: '#1976D2',
                        secondary: '#5EEAD4',
                        bo: '#1976D2'
                    }
                },
                buttons: {
                    confirm: {
                        variant: (themeOptions?.buttons?.confirm.variant ?? 'contained') as any,
                        borderRadius: themeOptions?.buttons?.confirm.radius ?? 4
                    },
                    cancel: {
                        variant: (themeOptions?.buttons?.cancel.variant ?? 'outlined') as any,
                        borderRadius: themeOptions?.buttons?.cancel.radius ?? 4
                    },
                    header: {
                        variant: (themeOptions?.buttons?.header.variant ?? 'text') as any,
                        borderRadius: themeOptions?.buttons?.header.radius ?? 4
                    },
                },
                inputs: themeOptions?.inputs ? {
                    texts: {
                        borderRadius: themeOptions?.inputs.textFields.radius,
                        variant: themeOptions?.inputs.textFields.variant
                    },
                    selectors: {
                        borderRadius: themeOptions?.inputs.selectors.radius,
                        variant: themeOptions?.inputs.selectors.variant
                    }
                } :
                    undefined,
                typography: {
                    h1: {
                        fontSize: themeOptions?.typography?.h1.fontSize ?? '6rem',
                        fontFamily: themeOptions?.typography?.h1.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h1.fontWeight ?? 300,
                        color: themeOptions?.typography?.h1.color,
                        textDecoration: themeOptions?.typography?.h1.textDecoration ?? 'none'
                    },
                    h2: {
                        fontSize: themeOptions?.typography?.h2.fontSize ?? '3.75rem',
                        fontFamily: themeOptions?.typography?.h2.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h2.fontWeight ?? 300,
                        color: themeOptions?.typography?.h2.color,
                        textDecoration: themeOptions?.typography?.h2.textDecoration ?? 'none'
                    },
                    h3: {
                        fontSize: themeOptions?.typography?.h3.fontSize ?? '3rem',
                        fontFamily: themeOptions?.typography?.h3.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h3.fontWeight ?? 700,
                        color: themeOptions?.typography?.h3.color,
                        textDecoration: themeOptions?.typography?.h3.textDecoration ?? 'none'
                    },
                    h4: {
                        fontSize: themeOptions?.typography?.h4.fontSize ?? '2.125rem',
                        fontFamily: themeOptions?.typography?.h4.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h4.fontWeight ?? 700,
                        color: themeOptions?.typography?.h4.color,
                        textDecoration: themeOptions?.typography?.h4.textDecoration ?? 'none'
                    },
                    h5: {
                        fontSize: themeOptions?.typography?.h5.fontSize ?? '1.5rem',
                        fontFamily: themeOptions?.typography?.h5.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h5.fontWeight ?? 700,
                        color: themeOptions?.typography?.h5.color,
                        textDecoration: themeOptions?.typography?.h5.textDecoration ?? 'none'
                    },
                    h6: {
                        fontSize: themeOptions?.typography?.h6.fontSize ?? '1.25rem',
                        fontFamily: themeOptions?.typography?.h6.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h6.fontWeight ?? 700,
                        color: themeOptions?.typography?.h6.color,
                        textDecoration: themeOptions?.typography?.h6.textDecoration ?? 'none'
                    },
                    subtitle1: {
                        fontSize: themeOptions?.typography?.subtitle1.fontSize ?? '1rem',
                        fontFamily: themeOptions?.typography?.subtitle1.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.h1.fontWeight ?? 400,
                        color: themeOptions?.typography?.subtitle1.color,
                        textDecoration: themeOptions?.typography?.subtitle1.textDecoration ?? 'none'
                    },
                    subtitle2: {
                        fontSize: themeOptions?.typography?.subtitle2.fontSize ?? '0.875rem',
                        fontFamily: themeOptions?.typography?.subtitle2.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.subtitle2.fontWeight ?? 400,
                        color: themeOptions?.typography?.subtitle2.color,
                        textDecoration: themeOptions?.typography?.subtitle2.textDecoration ?? 'none'
                    },
                    body1: {
                        fontSize: themeOptions?.typography?.body1.fontSize ?? '1rem',
                        fontFamily: themeOptions?.typography?.body1.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.body1.fontWeight ?? 400,
                        color: themeOptions?.typography?.body1.color,
                        textDecoration: themeOptions?.typography?.body1.textDecoration ?? 'none'
                    },
                    body2: {
                        fontSize: themeOptions?.typography?.body2.fontSize ?? '0.875rem',
                        fontFamily: themeOptions?.typography?.body2.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.body2.fontWeight ?? 400,
                        color: themeOptions?.typography?.body2.color,
                        textDecoration: themeOptions?.typography?.body2.textDecoration ?? 'none'
                    },
                    overline: {
                        fontSize: themeOptions?.typography?.overline.fontSize ?? '0.75rem',
                        fontFamily: themeOptions?.typography?.overline.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.overline.fontWeight ?? 400,
                        color: themeOptions?.typography?.overline.color,
                        textDecoration: themeOptions?.typography?.overline.textDecoration ?? 'none'
                    },
                    caption: {
                        fontSize: themeOptions?.typography?.caption.fontSize ?? '0.75rem',
                        fontFamily: themeOptions?.typography?.caption.fontFamily ?? 'MontSerrat',
                        fontWeight: themeOptions?.typography?.caption.fontWeight ?? 400,
                        color: themeOptions?.typography?.caption.color,
                        textDecoration: themeOptions?.typography?.caption.textDecoration ?? 'none'
                    }
                }
            },
            pickerText
        );
    }, [pickerText, themeObject, i18n]);

    useEffect(() => {
        (async () => {
            try {
                const object = await makeRequest();
                dispatch(setTheme(object));
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (themeObject) {
            const toBeLoadedFonts = uniqBy(
                Object.values(themeObject.data.typography ?? {}).filter((item) => {
                    return item.fontFamilyUrl && item.fontFamilyUrl.length > 0;
                }),
                (item) => item.fontFamilyUrl
            );
            for (const font of toBeLoadedFonts) {
                const styleElement = document.head.querySelector(`link[id="${font.fontFamily}-font"]`);
                if (!styleElement) {
                    const link = document.createElement('link');
                    link.id = font.fontFamily + '-font';
                    link.rel = 'stylesheet';
                    link.href = font.fontFamilyUrl!;
                    document.head.appendChild(link);
                }
            }
        }
    }, [themeObject]);

    useEffect(() => {
        // replace all #E6592F with primary color
        for (const stylesheet of document.styleSheets) {
            try {
                // get all css texts
                let css = '';
                for (const rule of stylesheet.cssRules) {
                    css += rule.cssText;
                }
                css = css.replace(/#E6592F/ig, theme.palette.primary.main);
                const node = stylesheet.ownerNode as HTMLStyleElement | null;
                if (node) {
                    node.innerHTML = css;
                }
            } catch (error) {
                if (!(error instanceof DOMException) || !error.name.includes('SecurityError')) {
                    throw error;
                }
            }
        }
    }, [theme]);

    return (
        <BaseThemeProvider theme={theme}>
            {props.children}
        </BaseThemeProvider>
    );
}

const makeRequest = memoizee(
    async function makeRequest(): Promise<ThemeObject | null> {
        const { pass_check, headers } = CheckBeforeRequest();

        if (pass_check) {
            const response = await axios.get<{ results: ThemeObject[] }>(
                `${API_HREF}client/${window.id_owner}/theme/`,
                {
                    headers,
                    params: {
                        is_active: true
                    }
                }
            );
            return response.data.results[0] ?? null;
        }

        return null;
    },
    { promise: true }
);
